import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { Link, useHistory } from "react-router-dom";
import Sidebar from '../Components/Sidebar/Sidebar';
import axios from 'axios';

import verificandoToken from '../../FuncoesGerais/VerificarToken';
import verificandoApp from '../../FuncoesGerais/verificaApp';
import { getAnalistaDisplayNome } from '../../FuncoesGerais/tipoAnalista';
import { ensureProtocol } from '../../config/backend';
import { useTiposChamado } from '../../hooks/useTiposChamado';
import { useStatusChamado } from '../../hooks/useStatusChamado';
import { isStatusChamadoFechado } from '../../api/statusChamado';
import useAutoRefresh from '../../hooks/useAutoRefresh';

import { ModalAlert, ModalConfirm } from '../Components/ModalSistema';
import { PageHeader, EmptyState, SkeletonCards } from '../Components/ui';
import DrawerChamadoAutomatico from './DrawerChamadoAutomatico';
import './home.css';

function ChamadoBadges({ statusCategory, statusLabel, prioridadeTag, sprintNome, sprintId, atendenteNome }) {
    const prioridadeMap = {
        P1: { label: 'P1 crítico', className: 'badge--prio-p1' },
        P2: { label: 'P2 alto', className: 'badge--prio-p2' },
        P3: { label: 'P3 médio', className: 'badge--prio-p3' },
        P4: { label: 'P4 baixo', className: 'badge--prio-p4' },
    };

    const statusClassMap = {
        aberto: 'badge--status-aberto',
        em_desenvolvimento: 'badge--status-dev',
        pausado: 'badge--status-pausado',
        concluido: 'badge--status-concluido',
        bloqueado: 'badge--status-bloqueado',
    };

    const prioridade = prioridadeTag && prioridadeMap[prioridadeTag];
    const statusClass = statusClassMap[statusCategory] || 'badge--status-aberto';
    const nomeAte = (atendenteNome || '').toString().trim();

    return (
        <div className="chamado-badges">
            {statusLabel && (
                <span className={['badge', statusClass].join(' ')}>
                    {statusLabel}
                </span>
            )}
            {nomeAte && (
                <span className="badge badge--atendente">
                    {nomeAte}
                </span>
            )}
            {prioridade && (
                <span className={['badge', prioridade.className].join(' ')}>
                    {prioridade.label}
                </span>
            )}
            {(sprintId || sprintNome) && (
                sprintId ? (
                    <Link to={`/sprint?sprint=${sprintId}`} className="badge badge--sprint badge--sprint-link" title="Ir para a sprint" onClick={(e) => e.stopPropagation()}>
                        {sprintNome && String(sprintNome).trim() ? sprintNome : `Sprint #${sprintId}`}
                    </Link>
                ) : (
                    <span className="badge badge--sprint">{sprintNome || 'Sprint'}</span>
                )
            )}
        </div>
    );
}

function ChamadoActions({ statusCategory, onIniciar, onPausar, onEditar, onMoreActions }) {
    const podeIniciar = statusCategory === 'aberto' || statusCategory === 'pausado';
    const podePausar = statusCategory === 'em_desenvolvimento';

    return (
        <div className="chamado-actions">
            {podeIniciar && (
                <button className="icon-button icon-button--play" onClick={onIniciar} title="Iniciar / pegar chamado" aria-label="Iniciar chamado">
                    <i className="fas fa-play" />
                </button>
            )}
            {podePausar && (
                <button className="icon-button icon-button--pause" onClick={onPausar} title="Pausar chamado" aria-label="Pausar chamado">
                    <i className="fas fa-pause" />
                </button>
            )}
            <button className="icon-button" onClick={onEditar} title="Abrir / editar chamado" aria-label="Abrir chamado">
                <i className="fas fa-pen" />
            </button>
            <button className="icon-button" onClick={onMoreActions} title="Mais ações" aria-label="Mais ações">
                <i className="fas fa-ellipsis" />
            </button>
        </div>
    );
}

function ChamadoCard({ chamado, getNomeTipo, getNomeStatus, onIniciar, onPausar, onEditar, onMoreActions, onTrocarPrioridade, statusCodigosEmContagem }) {
    const history = useHistory();
    const draggingRef = useRef(false);
    const [dragOver, setDragOver] = useState(false);

    const tipoNome = getNomeTipo(chamado.tipo);
    const tipoEmoji = (chamado.tipo_emoji && String(chamado.tipo_emoji).trim()) || null;
    const tipoCor = (chamado.tipo_cor && String(chamado.tipo_cor).trim()) || null;
    const statusLabel = getNomeStatus(chamado.status) || '';

    const statusCategory = useMemo(() => {
        const lower = (statusLabel || '').toLowerCase();
        if (lower.includes('bloque')) return 'bloqueado';
        if (lower.includes('paus')) return 'pausado';
        if (lower.includes('desenvolv')) return 'em_desenvolvimento';
        if (lower.includes('conclu')) return 'concluido';
        return 'aberto';
    }, [statusLabel]);

    const prioridadeTag = chamado.prioridade ? `P${chamado.prioridade}` : null;

    const isAtivo = statusCategory === 'em_desenvolvimento';
    const statusNum = chamado.status != null ? Number(chamado.status) : null;
    const isEmContagem = useMemo(() => {
        if (!statusCodigosEmContagem || statusCodigosEmContagem.length === 0 || statusNum == null) return false;
        return statusCodigosEmContagem.some((c) => Number(c) === statusNum);
    }, [statusCodigosEmContagem, statusNum]);

    const formattedDate = useMemo(() => {
        if (!chamado.data) return '';
        const d = new Date(chamado.data);
        if (Number.isNaN(d.getTime())) return '';
        return d.toLocaleDateString('pt-BR', { day: '2-digit', month: 'short', year: 'numeric' });
    }, [chamado.data]);

    const handleClick = (e) => {
        if (e.target.closest('button, a, input, select')) return;
        if (draggingRef.current) return;
        history.push('/historicoSati/' + chamado.id);
    };

    const handleDragStart = (e) => {
        draggingRef.current = true;
        e.dataTransfer.setData('application/json', JSON.stringify({ id: chamado.id }));
        e.dataTransfer.effectAllowed = 'move';
        e.dataTransfer.setDragImage(e.currentTarget, 0, 0);
    };

    const handleDragEnd = () => {
        setTimeout(() => { draggingRef.current = false; }, 50);
        setDragOver(false);
    };

    const handleDragOver = (e) => {
        e.preventDefault();
        e.dataTransfer.dropEffect = 'move';
        setDragOver(true);
    };

    const handleDragLeave = () => {
        setDragOver(false);
    };

    const handleDrop = (e) => {
        e.preventDefault();
        setDragOver(false);
        const raw = e.dataTransfer.getData('application/json');
        if (!raw || !onTrocarPrioridade) return;
        try {
            const { id: draggedId } = JSON.parse(raw);
            if (Number(draggedId) !== Number(chamado.id)) onTrocarPrioridade(draggedId, chamado.id);
        } catch (_) {}
    };

    const areaLabel = chamado.area === 'I'
        ? 'Implantação'
        : chamado.area === 'S'
            ? 'Suporte'
            : (chamado.area || 'Área não definida');

    const sprintNome = (chamado.sprint_nome && String(chamado.sprint_nome).trim()) || null;
    const sprintId = chamado.sprint_id != null && chamado.sprint_id !== '' && !Number.isNaN(Number(chamado.sprint_id)) ? Number(chamado.sprint_id) : null;

    const cardStyle = tipoCor ? { '--card-cor': tipoCor } : undefined;

    return (
        <div
            className={['chamado-card', isAtivo ? 'chamado-card--ativo' : '', isEmContagem ? 'chamado-card--em-contagem' : '', tipoCor ? 'chamado-card--com-cor' : '', dragOver ? 'chamado-card--drag-over' : ''].join(' ')}
            style={cardStyle}
            onClick={handleClick}
            draggable={!!onTrocarPrioridade}
            onDragStart={onTrocarPrioridade ? handleDragStart : undefined}
            onDragEnd={onTrocarPrioridade ? handleDragEnd : undefined}
            onDragOver={onTrocarPrioridade ? handleDragOver : undefined}
            onDragLeave={onTrocarPrioridade ? handleDragLeave : undefined}
            onDrop={onTrocarPrioridade ? handleDrop : undefined}
            title={onTrocarPrioridade ? 'Arraste para cima ou para baixo para alterar a prioridade' : undefined}
        >
            <div className="chamado-card__main">
                <div className="chamado-card__title-row">
                    <span className="chamado-card__icon">{tipoEmoji || '💻'}</span>
                    <div className="chamado-card__title-text">
                        <div className="chamado-card__tipo">
                            <span className="chamado-card__id">#{chamado.id}</span>
                            {tipoNome && <span className="chamado-card__tipo-nome"> · {tipoNome}</span>}
                        </div>
                        <div className="chamado-card__titulo">
                            {chamado.descricao?.trim() || 'Chamado sem descrição'}
                        </div>
                    </div>
                </div>

                <div className="chamado-card__meta-line">
                    <span>{getAnalistaDisplayNome(chamado.nomeAte || 'Sem responsável', { tipo_analista: chamado.tipo_analista_atendente, eh_suporte: chamado.atendente_eh_suporte, eh_desenvolvedor: chamado.atendente_eh_desenvolvedor })}</span>
                    <span>•</span>
                    <span>{formattedDate || 'Data não informada'}</span>
                    <span>•</span>
                    <span>{areaLabel}</span>
                    {(chamado.nomSolicitante && String(chamado.nomSolicitante).trim()) && (
                        <>
                            <span>•</span>
                            <span>{String(chamado.nomSolicitante).trim()}</span>
                        </>
                    )}
                </div>

                <ChamadoBadges
                    statusCategory={statusCategory}
                    statusLabel={statusLabel}
                    prioridadeTag={prioridadeTag}
                    sprintNome={sprintNome}
                    sprintId={sprintId}
                    atendenteNome={chamado.nomeAte}
                />
            </div>

            <ChamadoActions
                statusCategory={statusCategory}
                onIniciar={(e) => { e.stopPropagation(); onIniciar(chamado); }}
                onPausar={(e) => { e.stopPropagation(); onPausar(chamado); }}
                onEditar={(e) => { e.stopPropagation(); onEditar(chamado); }}
                onMoreActions={(e) => { e.stopPropagation(); onMoreActions(chamado); }}
            />
        </div>
    );
}

function ChamadosList({ title, items, loading, emptyMessage, getNomeTipo, getNomeStatus, onIniciar, onPausar, onEditar, onMoreActions, onTrocarPrioridade, statusCodigosEmContagem }) {
    return (
        <div className="chamados-list">
            {loading && items.length === 0 ? (
                <SkeletonCards count={3} />
            ) : null}
            {!loading && items.length === 0 ? (
                <EmptyState
                    icon="fas fa-inbox"
                    title="Nenhum chamado por aqui"
                    description={emptyMessage || `Nenhum chamado em ${title.toLowerCase()}.`}
                />
            ) : null}
            {items.map((chamado) => (
                <ChamadoCard
                    key={chamado.id}
                    chamado={chamado}
                    getNomeTipo={getNomeTipo}
                    getNomeStatus={getNomeStatus}
                    onIniciar={onIniciar}
                    onPausar={onPausar}
                    onEditar={onEditar}
                    onMoreActions={onMoreActions}
                    onTrocarPrioridade={onTrocarPrioridade}
                    statusCodigosEmContagem={statusCodigosEmContagem}
                />
            ))}
        </div>
    );
}

function chaveStatus(status) {
    if (status == null) return null;
    const legado = status.codigo_legado != null && status.codigo_legado !== '' ? Number(status.codigo_legado) : NaN;
    if (!Number.isNaN(legado)) return legado;
    const id = status.id != null && status.id !== '' ? Number(status.id) : NaN;
    return Number.isNaN(id) ? null : id;
}

function chamadoNaColuna(chamado, status) {
    const n = chamado?.status != null && chamado.status !== '' ? Number(chamado.status) : NaN;
    if (Number.isNaN(n)) return false;
    const legado = status.codigo_legado != null && status.codigo_legado !== '' ? Number(status.codigo_legado) : NaN;
    const id = status.id != null && status.id !== '' ? Number(status.id) : NaN;
    return n === legado || n === id;
}

function ChamadoKanbanCard({ chamado, onDragStartCard, onDragEndCard }) {
    const history = useHistory();
    const draggingRef = useRef(false);
    const tipoCor = (chamado.tipo_cor && String(chamado.tipo_cor).trim()) || '';
    const atendente = (chamado.nomeAte && String(chamado.nomeAte).trim()) || 'Sem responsável';
    const resumo = (chamado.descricao && String(chamado.descricao).trim()) || 'Sem descrição';

    return (
        <div
            className="kanban-card"
            style={tipoCor ? { borderLeftColor: tipoCor } : undefined}
            draggable
            onClick={() => {
                if (draggingRef.current) return;
                history.push('/historicoSati/' + chamado.id);
            }}
            onDragStart={(e) => {
                draggingRef.current = true;
                const payload = JSON.stringify({ id: chamado.id, status: chamado.status });
                e.dataTransfer.setData('application/json', payload);
                e.dataTransfer.setData('text/plain', payload);
                e.dataTransfer.effectAllowed = 'move';
                onDragStartCard?.({ id: chamado.id, status: chamado.status });
            }}
            onDragEnd={() => {
                setTimeout(() => { draggingRef.current = false; }, 50);
                onDragEndCard?.();
            }}
            title={`${atendente} — ${resumo}`}
        >
            <div className="kanban-card__linha">
                <span className="kanban-card__num">#{chamado.id}</span>
                <span className="kanban-card__ate">{atendente}</span>
            </div>
            <p className="kanban-card__resumo">{resumo}</p>
        </div>
    );
}

function ChamadosKanban({ colunas, itensPorColuna, outros, loading, onMoverStatus }) {
    const [dragOverKey, setDragOverKey] = useState(null);
    const [arrastando, setArrastando] = useState(null);

    const renderCard = (chamado) => (
        <ChamadoKanbanCard
            key={chamado.id}
            chamado={chamado}
            onDragStartCard={setArrastando}
            onDragEndCard={() => {
                setArrastando(null);
                setDragOverKey(null);
            }}
        />
    );

    const soltarNaColuna = (e, coluna) => {
        e.preventDefault();
        setDragOverKey(null);
        const raw = e.dataTransfer.getData('application/json') || e.dataTransfer.getData('text/plain');
        if (!raw || !onMoverStatus) return;
        try {
            const payload = JSON.parse(raw);
            if (payload?.id == null || chamadoNaColuna({ status: payload.status }, coluna)) return;
            const destino = chaveStatus(coluna);
            if (destino == null) return;
            onMoverStatus(payload.id, payload.status, destino);
        } catch (_) {}
    };

    if (loading && colunas.length === 0) {
        return <SkeletonCards count={3} />;
    }

    if (!loading && colunas.length === 0 && outros.length === 0) {
        return (
            <EmptyState
                icon="fas fa-columns"
                title="Nenhum status para o quadro"
                description="Cadastre status de chamado que não sejam de finalização para montar as colunas."
            />
        );
    }

    return (
        <div className="chamados-kanban">
            {colunas.map((col) => {
                const key = chaveStatus(col);
                const itens = itensPorColuna.get(key) || [];
                const origem = arrastando && chamadoNaColuna({ status: arrastando.status }, col);
                return (
                    <section
                        key={key}
                        className={`chamados-kanban-coluna${dragOverKey === key && !origem ? ' chamados-kanban-coluna--over' : ''}`}
                        aria-label={col.nome}
                        onDragOver={(e) => {
                            if (origem) return;
                            e.preventDefault();
                            e.dataTransfer.dropEffect = 'move';
                            if (dragOverKey !== key) setDragOverKey(key);
                        }}
                        onDragLeave={(e) => {
                            if (!e.currentTarget.contains(e.relatedTarget)) {
                                setDragOverKey((atual) => (atual === key ? null : atual));
                            }
                        }}
                        onDrop={(e) => soltarNaColuna(e, col)}
                    >
                        <header className="chamados-kanban-coluna__head">
                            <span className="chamados-kanban-coluna__nome">{col.nome || 'Status'}</span>
                            <span className="chamados-kanban-coluna__count">{itens.length}</span>
                        </header>
                        <div className="chamados-kanban-coluna__cards">
                            {itens.length === 0 ? (
                                <p className="chamados-kanban-vazio">Nenhum chamado</p>
                            ) : itens.map(renderCard)}
                        </div>
                    </section>
                );
            })}
            {outros.length > 0 && (
                <section
                    className="chamados-kanban-coluna"
                    aria-label="Outros status"
                    onDragOver={(e) => e.preventDefault()}
                >
                    <header className="chamados-kanban-coluna__head">
                        <span className="chamados-kanban-coluna__nome">Outros</span>
                        <span className="chamados-kanban-coluna__count">{outros.length}</span>
                    </header>
                    <div className="chamados-kanban-coluna__cards">
                        {outros.map(renderCard)}
                    </div>
                </section>
            )}
        </div>
    );
}

function ChamadosMetrics({ metrics }) {
    const cards = [
        { key: 'abertos', label: 'Abertos', value: metrics.abertos, icon: 'fas fa-inbox', tone: 'neutral' },
        { key: 'emAtendimento', label: 'Em atendimento', value: metrics.emAtendimento, icon: 'fas fa-headset', tone: 'blue' },
        { key: 'emDesenvolvimento', label: 'Em desenvolvimento', value: metrics.emDesenvolvimento, icon: 'fas fa-code', tone: 'indigo' },
        { key: 'emAnalise', label: 'Em análise', value: metrics.emAnalise, icon: 'fas fa-magnifying-glass', tone: 'cyan' },
        { key: 'emTestes', label: 'Em testes', value: metrics.emTestes, icon: 'fas fa-vial', tone: 'purple' },
        { key: 'pausados', label: 'Pausados', value: metrics.pausados, icon: 'fas fa-pause', tone: 'orange' },
        { key: 'concluidosHoje', label: 'Concluídos hoje', value: metrics.concluidosHoje, icon: 'fas fa-circle-check', tone: 'green' },
    ];
    return (
        <div className="chamados-metrics">
            {cards.map((c) => (
                <div key={c.key} className={`chamados-metric-card chamados-metric-card--${c.tone}`}>
                    <span className="chamados-metric-icon"><i className={c.icon} aria-hidden="true" /></span>
                    <div className="chamados-metric-body">
                        <div className="chamados-metric-value">{c.value}</div>
                        <div className="chamados-metric-label">{c.label}</div>
                    </div>
                </div>
            ))}
        </div>
    );
}

function ChamadosHeader({ search, onSearchChange, loading, onAtualizar, onAutomatico, filtrosAbertos, onToggleFiltros, filtrosAtivos, totalVisivel, vista, onVistaChange }) {
    return (
        <>
            <PageHeader
                title="Chamados em atendimento"
                subtitle="Acompanhe, atenda e priorize seus chamados e os da equipe."
                icon="fas fa-headset"
                actions={(
                    <>
                        <Link to='/novaSati' className="ax-btn ax-btn--primary" type="button">
                            <i className="fas fa-plus" /> Novo chamado
                        </Link>
                        <button onClick={onAutomatico} className="ax-btn" type="button">
                            <i className="fas fa-bolt" /> Automático
                        </button>
                        <button onClick={onAtualizar} className="ax-btn ax-btn--icon" type="button" disabled={loading} title="Atualizar lista" aria-label="Atualizar lista">
                            <i className={`fas fa-arrows-rotate ${loading ? 'fa-spin' : ''}`} />
                        </button>
                    </>
                )}
            />
            <div className="chamados-toolbar">
                <div className="chamados-search">
                    <i className="fas fa-search chamados-search__icon" aria-hidden="true" />
                    <input
                        type="search"
                        placeholder="Buscar por nº, descrição, atendente, solicitante ou sprint..."
                        value={search}
                        onChange={(e) => onSearchChange(e.target.value)}
                        aria-label="Buscar nos chamados carregados"
                    />
                    {typeof totalVisivel === 'number' && (
                        <span className="chamados-search__count">{totalVisivel}</span>
                    )}
                </div>
                <div className="chamados-vista" role="group" aria-label="Formato da visualização">
                    <button
                        type="button"
                        className={vista === 'lista' ? 'is-active' : ''}
                        onClick={() => onVistaChange('lista')}
                        aria-pressed={vista === 'lista'}
                    >
                        <i className="fas fa-list" /> Lista
                    </button>
                    <button
                        type="button"
                        className={vista === 'kanban' ? 'is-active' : ''}
                        onClick={() => onVistaChange('kanban')}
                        aria-pressed={vista === 'kanban'}
                    >
                        <i className="fas fa-table-columns" /> Quadro
                    </button>
                </div>
                <button
                    type="button"
                    className={`ax-btn ${filtrosAbertos || filtrosAtivos ? 'ax-btn--active' : ''}`}
                    onClick={onToggleFiltros}
                    aria-expanded={filtrosAbertos}
                >
                    <i className="fas fa-sliders" /> Filtros
                    {filtrosAtivos ? <span className="chamados-filtros-dot" aria-hidden="true" /> : null}
                    <i className={`fas fa-chevron-${filtrosAbertos ? 'up' : 'down'} chamados-filtros-caret`} aria-hidden="true" />
                </button>
            </div>
        </>
    );
}

function ChamadosPage() {

    let user = localStorage.getItem('codUsu');

    const [data, setData] = useState([]);
    const [texto, setTexto] = useState('');
    const [colabo, setColabo] = useState('');
    const [solici, setSolici] = useState('');
    const [marcado, setMarcado] = useState(false);
    const [loading, setLoading] = useState(false);
    const [search, setSearch] = useState('');
    const [dataComoColaborador, setDataComoColaborador] = useState([]);
    const [listaResponsavelAberta, setListaResponsavelAberta] = useState(true);
    const [listaColaboradorAberta, setListaColaboradorAberta] = useState(true);
    const [filtrosAbertos, setFiltrosAbertos] = useState(false);
    const [erro, setErro] = useState('');
    const [vista, setVista] = useState(() => (localStorage.getItem('homeChamadosVista') === 'kanban' ? 'kanban' : 'lista'));

    const [, setStatusCodesEmContagem] = useState([]);

    const [, setStatusCodesDesenvolvimento] = useState([]);

    const [statusEmAtendimentoConfig, setStatusEmAtendimentoConfig] = useState(null);
    const [statusEmDesenvolvimentoConfig, setStatusEmDesenvolvimentoConfig] = useState(null);
    const [statusPausadoConfig, setStatusPausadoConfig] = useState(null);
    const [statusEmAnaliseConfig, setStatusEmAnaliseConfig] = useState(null);
    const [statusEmTesteConfig, setStatusEmTesteConfig] = useState(null);

    const [concluidosHojeApi, setConcluidosHojeApi] = useState(0);

    const [isAutomatico, setAutomatico] = useState(false);
    const [confirmPegar, setConfirmPegar] = useState({ show: false, sati: null });
    const [confirmPausar, setConfirmPausar] = useState({ show: false, sati: null });
    const [alertMsg, setAlertMsg] = useState({ show: false, message: '' });

    const history = useHistory();
    const { tipos, getNome: getNomeTipo } = useTiposChamado();
    const { statusList, loading: loadingStatus, getNome: getNomeStatus } = useStatusChamado();

    const tipoIdsUsaSprint = useMemo(() => {
        const list = tipos || [];
        return new Set(list.filter((t) => t.usa_sprint === 1).map((t) => Number(t.id)));
    }, [tipos]);

    const verificar = async () => {
        const resultado = await verificandoApp("ATENDENTE");

        if (!resultado) {
            history.push("/");
        }
    };

    const openAutomatico = () => {
        setAutomatico(true);
    };

    const fecharAutomatico = () => {
        setAutomatico(false);
    };

    const getDataHojeISO = () => {
        const hoje = new Date();
        const y = hoje.getFullYear();
        const m = String(hoje.getMonth() + 1).padStart(2, '0');
        const d = String(hoje.getDate()).padStart(2, '0');
        return `${y}-${m}-${d}`;
    };

    const fetchData = async (silencioso = false) => {
        verificar();
        setErro('');
        if (!silencioso) setLoading(true);
        const descr = (texto || '').trim();
        let colab;
        verificandoToken();
        const token = localStorage.getItem('token');
        const baseUrl = ensureProtocol(localStorage.getItem("ipBackend2"));

        if ((colabo || '').trim().length > 0) {
            colab = String(colabo).trim() + ',1';
        } else if (marcado) {
            try {
                const resEquipe = await axios.get(baseUrl + '/usuarios/equipe-visualizacao', { headers: { Authorization: token } });
                const equipeIds = (resEquipe.data?.equipe_ids ?? '').toString().trim();
                const ids = equipeIds ? equipeIds.split(',').map((c) => c.trim()).filter(Boolean) : [];
                if (ids.length > 1) {
                    colab = '1,' + equipeIds + ',' + user;
                } else {
                    const resAnalistas = await axios.get(baseUrl + '/usuarios/analistas', { headers: { Authorization: token } });
                    const analistas = Array.isArray(resAnalistas.data) ? resAnalistas.data : [];
                    const idsAnalistas = analistas.map((u) => String(u.USUSEQ ?? u.ususeq ?? u.id ?? '').trim()).filter(Boolean);
                    colab = idsAnalistas.length > 0 ? '1,' + idsAnalistas.join(',') : '1,' + user;
                }
            } catch (eEquipe) {
                console.error('Erro ao buscar equipe:', eEquipe);
                colab = '1,' + user;
            }
        } else {
            colab = '1,' + user;
        }

        try {
            const [resResp, resColab, resMetricas] = await Promise.all([
                axios.get(baseUrl + "/satis", {
                    headers: { Authorization: `${token}` },
                    params: { user: user, descr: descr, colab: colab, solici: (solici || '').trim() }
                }),
                axios.get(baseUrl + "/satis/como-colaborador", {
                    headers: { Authorization: `${token}` },
                    params: { descr: descr }
                }),
                axios.get(baseUrl + "/satis/metricas-home", {
                    headers: { Authorization: `${token}` },
                    params: (() => {
                        const dataHoje = getDataHojeISO();
                        const p = { dataHoje };
                        if (marcado && colab) {
                            p.equipe = colab;
                        }
                        return p;
                    })()
                }).catch(() => ({ data: { concluidosHoje: null } }))
            ]);

            const rawResp = Array.isArray(resResp.data) ? resResp.data : [];
            const rawColab = Array.isArray(resColab.data) ? resColab.data : [];

            const mapRow = (r) => ({
                id: r.id,
                descricao: r.descricao || '',
                area: r.SATARESOL,
                data: r.data,
                status: r.SATSTASAI,
                atendente: r.atendente,
                solicitante: r.codSolicitante,
                prioridade: r.SATPRINUM,
                tipo: r.SATTIP ?? r.tipo,
                dptcod: r.DPTCOD,
                nomeAte: r.USRNOMCPL,
                atendente_eh_suporte: r.atendente_eh_suporte ?? r.eh_suporte,
                atendente_eh_desenvolvedor: r.atendente_eh_desenvolvedor ?? r.eh_desenvolvedor,
                tipo_analista_atendente: r.tipo_analista_atendente ?? r.atendente_tipo_analista,
                nomSolicitante: r.solicitante,
                sprint_data_inicio: r.sprint_data_inicio ?? r.SPRINT_DATA_INICIO,
                sprint_data_fim: r.sprint_data_fim ?? r.SPRINT_DATA_FIM,
                sprint_id: r.sprint_id ?? r.SPRINT_ID,
                sprint_nome: (r.sprint_nome ?? r.SPRINT_NOME ?? '').toString().trim() || null,
                tipo_emoji: r.tipo_emoji != null ? String(r.tipo_emoji).trim() || null : null,
                tipo_cor: r.tipo_cor != null ? String(r.tipo_cor).trim() || null : null
            });

            setData(rawResp.map(mapRow));
            setDataComoColaborador(rawColab.map(mapRow));
            const m = resMetricas?.data;
            let concluidosHoje = m && typeof m.concluidosHoje === 'number' ? m.concluidosHoje : null;
            if (concluidosHoje === null) {
                const dataHoje = getDataHojeISO();
                try {
                    const resFechadas = await axios.get(baseUrl + "/satis/fechadas", {
                        headers: { Authorization: `${token}` },
                        params: { user, descr: descr || ' ', colab }
                    });
                    const lista = Array.isArray(resFechadas.data) ? resFechadas.data : [];
                    const dataHojeStr = dataHoje;
                    concluidosHoje = lista.filter((item) => {
                        const dataFec = item.data ?? item.SATDATFEC ?? item.satdatfec ?? '';
                        if (!dataFec) return false;
                        const str = typeof dataFec === 'string' ? dataFec : (dataFec instanceof Date ? dataFec.toISOString() : String(dataFec));
                        const dia = str.slice(0, 10);
                        return dia === dataHojeStr;
                    }).length;
                } catch (_) {
                    concluidosHoje = 0;
                }
            }
            setConcluidosHojeApi(concluidosHoje);
        } catch (error) {
            setData([]);
            setDataComoColaborador([]);
            setConcluidosHojeApi(0);
            if (error?.response?.status === 404) {
                setErro('');
            } else {
                setErro(error?.response?.status === 401 ? 'Sessão expirada. Faça login novamente.' : (error?.response?.data?.error || 'Não foi possível carregar os chamados. Tente novamente.'));
            }
            console.error('Erro ao buscar dados:', error);
        } finally {
            setLoading(false);
        }
    };

    // Fase 8: atualiza a lista de abertos sozinha (silencioso, pausa em aba oculta).
    useAutoRefresh(() => fetchData(true), 30000, true);

    const executarIniciarChamado = async (sati, apenasReassign = false) => {
        if (!sati) return;
        const satNum = sati?.id ?? sati;
        const contexto = tipoIdsUsaSprint.has(Number(sati?.tipo)) ? 'desenvolvimento' : 'atendimento';
        const colaboUser = localStorage.getItem('codUsu');
        try {
            verificandoToken();
            const token = localStorage.getItem('token');
            await axios.put(
                ensureProtocol(localStorage.getItem("ipBackend2")) + "/satis/pegar",
                { colabo: colaboUser, satNum, contexto, apenasReassign: !!apenasReassign },
                { headers: { Authorization: `${token}` } }
            );
            await fetchData();
        } catch (error) {
            console.error('Erro ao iniciar chamado:', error);
            setAlertMsg({ show: true, message: error?.response?.data?.error || 'Não foi possível iniciar. Tente novamente.' });
        }
    };

    const handleIniciarChamado = (sati) => {
        const codUsu = localStorage.getItem('codUsu');
        const jaSouResponsavel = codUsu != null && (String(sati?.atendente) === String(codUsu) || Number(sati?.atendente) === Number(codUsu));
        if (jaSouResponsavel) {
            executarIniciarChamado(sati);
        } else {
            setConfirmPegar({ show: true, sati });
        }
    };

    const executarPegarChamado = async () => {
        const sati = confirmPegar.sati;
        setConfirmPegar({ show: false, sati: null });
        await executarIniciarChamado(sati, true);
    };

    const handlePausarChamado = (sati) => {
        setConfirmPausar({ show: true, sati });
    };

    const executarPausarChamado = async () => {
        const sati = confirmPausar.sati;
        setConfirmPausar({ show: false, sati: null });
        if (!sati) return;
        const satNum = sati?.id ?? sati;
        const contexto = tipoIdsUsaSprint.has(Number(sati?.tipo)) ? 'desenvolvimento' : 'atendimento';
        try {
            verificandoToken();
            const token = localStorage.getItem('token');
            await axios.put(
                ensureProtocol(localStorage.getItem("ipBackend2")) + "/satis/pausar",
                { satNum, contexto },
                { headers: { Authorization: `${token}` } }
            );
            await fetchData();
        } catch (error) {
            console.error('Erro ao pausar chamado:', error);
        }
    };

    const handleEditarChamado = (sati) => {
        const satNum = sati?.id ?? sati;
        history.push('/historicoSati/' + satNum);
    };

    const handleMoreActions = (sati) => {
        const satNum = sati?.id ?? sati;
        history.push('/historicoSati/' + satNum);
    };

    const moverStatusKanban = async (satNum, statusAntigo, statusNovo) => {
        if (satNum == null || statusNovo == null || Number(statusAntigo) === Number(statusNovo)) return;
        verificandoToken();
        const token = localStorage.getItem('token');
        const baseUrl = ensureProtocol(localStorage.getItem('ipBackend2'));
        if (!baseUrl) return;
        const aplicar = (lista) => lista.map((c) => (Number(c.id) === Number(satNum) ? { ...c, status: statusNovo } : c));
        setData((prev) => aplicar(prev));
        setDataComoColaborador((prev) => aplicar(prev));
        try {
            await axios.put(
                baseUrl + '/satis/status',
                { satStaSai: Number(statusNovo), old: statusAntigo, satNum },
                { headers: { Authorization: `${token}` } }
            );
        } catch (error) {
            console.error('Erro ao alterar status pelo quadro:', error);
            setAlertMsg({ show: true, message: error?.response?.data?.error || 'Não foi possível alterar o status do chamado.' });
        }
        await fetchData(true);
    };

    const handleTrocarPrioridade = async (satNum1, satNum2) => {
        if (satNum1 == null || satNum2 == null || Number(satNum1) === Number(satNum2)) return;
        verificandoToken();
        const token = localStorage.getItem('token');
        const baseUrl = ensureProtocol(localStorage.getItem('ipBackend2'));
        if (!baseUrl) return;
        try {
            await axios.put(baseUrl + '/satis/prioridade-trocar', { satNum1, satNum2 }, { headers: { Authorization: `${token}` } });
            await fetchData();
        } catch (error) {
            console.error('Erro ao trocar prioridade:', error);
            setAlertMsg({ show: true, message: error?.response?.data?.error || 'Não foi possível trocar a prioridade. Tente novamente.' });
        }
    };

    const fetchConfigStatus = useCallback(async () => {
        verificandoToken();
        const token = localStorage.getItem('token');
        const baseUrl = ensureProtocol(localStorage.getItem('ipBackend2'));
        if (!baseUrl || !token) return;
        try {
            const res = await axios.get(baseUrl + '/usuarios/config-status', { headers: { Authorization: token } });
            const d = res.data || {};
            setStatusEmAtendimentoConfig(d.status_em_atendimento_codigo_legado != null ? Number(d.status_em_atendimento_codigo_legado) : null);
            setStatusEmDesenvolvimentoConfig(d.status_em_desenvolvimento_codigo_legado != null ? Number(d.status_em_desenvolvimento_codigo_legado) : null);
            setStatusPausadoConfig(d.status_pausado_codigo_legado != null ? Number(d.status_pausado_codigo_legado) : null);
            setStatusEmAnaliseConfig(d.status_em_analise_codigo_legado != null ? Number(d.status_em_analise_codigo_legado) : null);
            setStatusEmTesteConfig(d.status_em_teste_codigo_legado != null ? Number(d.status_em_teste_codigo_legado) : null);
        } catch (_) {
            setStatusEmAtendimentoConfig(null);
            setStatusEmDesenvolvimentoConfig(null);
            setStatusPausadoConfig(null);
            setStatusEmAnaliseConfig(null);
            setStatusEmTesteConfig(null);
        }
    }, []);

    const fetchEtapasChamado = async () => {
        const empresaId = localStorage.getItem('empresa_id');
        if (!empresaId) return;
        verificandoToken();
        const token = localStorage.getItem('token');
        try {
            const res = await axios.get(ensureProtocol(localStorage.getItem('ipBackend2')) + '/empresas/' + empresaId + '/etapas-chamado', {
                headers: { Authorization: token }
            });
            const etapas = res.data?.etapas || {};
            const suporte = Array.isArray(etapas.SUPORTE) ? etapas.SUPORTE : [];
            const analise = Array.isArray(etapas.ANALISE) ? etapas.ANALISE : [];
            const desenvolvimento = Array.isArray(etapas.DESENVOLVIMENTO) ? etapas.DESENVOLVIMENTO : [];
            const todos = [...suporte, ...analise, ...desenvolvimento].map((c) => Number(c)).filter((c) => !Number.isNaN(c));
            const soDesenvolvimento = desenvolvimento.map((c) => Number(c)).filter((c) => !Number.isNaN(c));
            setStatusCodesEmContagem(todos);
            setStatusCodesDesenvolvimento(soDesenvolvimento);
        } catch (_) {
            setStatusCodesEmContagem([]);
            setStatusCodesDesenvolvimento([]);
        }
    };

    useEffect(() => {
        fetchEtapasChamado();
        fetchConfigStatus();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        fetchData();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        try {
            localStorage.setItem('homeChamadosVista', vista);
        } catch (_) {}
    }, [vista]);

    const handleCheckboxChange = (e) => {
        setMarcado(e.target.checked);
    };

    const filterBySearch = useCallback(
        (items) => {
            if (!search) return items;
            const s = search.toLowerCase();
            return items.filter((item) =>
                [
                    item.descricao,
                    item.area,
                    item.nomeAte,
                    item.nomSolicitante,
                    item.sprint_nome,
                    item.id != null ? String(item.id) : '',
                ]
                    .filter(Boolean)
                    .some((field) => String(field).toLowerCase().includes(s))
            );
        },
        [search]
    );

    const meusFiltrados = filterBySearch(data);
    const colaboradorFiltrados = filterBySearch(dataComoColaborador);
    const filtrosAtivos = Boolean((texto || '').trim() || (colabo || '').trim() || (solici || '').trim() || marcado);

    const colunasKanban = useMemo(() => {
        return (statusList || [])
            .filter((s) => s.ativo == null || Number(s.ativo) === 1)
            .filter((s) => !isStatusChamadoFechado([s], s.codigo_legado != null ? s.codigo_legado : s.id))
            .slice()
            .sort((a, b) => {
                const ordem = (Number(a.ordem) || 0) - (Number(b.ordem) || 0);
                if (ordem !== 0) return ordem;
                return String(a.nome || '').localeCompare(String(b.nome || ''), 'pt-BR');
            });
    }, [statusList]);

    const quadroKanban = useMemo(() => {
        const mapa = new Map();
        colunasKanban.forEach((s) => {
            const key = chaveStatus(s);
            if (key != null) mapa.set(key, []);
        });
        const outros = [];
        if (!statusList || statusList.length === 0) {
            return { mapa, outros };
        }
        const vistos = new Set();
        const todos = [
            ...meusFiltrados.map((c) => ({ ...c, papel: 'responsavel' })),
            ...colaboradorFiltrados.map((c) => ({ ...c, papel: 'colaborador' })),
        ];
        todos.forEach((c) => {
            const id = Number(c.id);
            if (vistos.has(id)) return;
            vistos.add(id);
            if (isStatusChamadoFechado(statusList, c.status)) return;
            const coluna = colunasKanban.find((s) => chamadoNaColuna(c, s));
            if (!coluna) {
                outros.push(c);
                return;
            }
            const key = chaveStatus(coluna);
            const lista = mapa.get(key);
            if (lista) lista.push(c);
            else outros.push(c);
        });
        return { mapa, outros };
    }, [colunasKanban, meusFiltrados, colaboradorFiltrados, statusList]);

    const totalVisivel = vista === 'kanban'
        ? [...quadroKanban.mapa.values()].reduce((acc, lista) => acc + lista.length, 0) + quadroKanban.outros.length
        : meusFiltrados.length + colaboradorFiltrados.length;

    const statusCodigosEmContagem = useMemo(() => {
        const list = [];
        if (statusEmAtendimentoConfig != null) list.push(statusEmAtendimentoConfig);
        if (statusEmDesenvolvimentoConfig != null) list.push(statusEmDesenvolvimentoConfig);
        if (statusEmAnaliseConfig != null) list.push(statusEmAnaliseConfig);
        if (statusEmTesteConfig != null) list.push(statusEmTesteConfig);
        return list;
    }, [statusEmAtendimentoConfig, statusEmDesenvolvimentoConfig, statusEmAnaliseConfig, statusEmTesteConfig]);

    const metrics = useMemo(() => {
        const all = [...data, ...dataComoColaborador];
        let abertos = 0;
        let emAtendimento = 0;
        let emDesenvolvimento = 0;
        let emAnalise = 0;
        let emTestes = 0;
        let pausados = 0;

        all.forEach((item) => {
            const nome = (getNomeStatus(item.status) || '').toLowerCase();
            const statusNum = Number(item.status);

            const ehPausado = statusPausadoConfig != null ? statusNum === statusPausadoConfig : nome.includes('paus');
            const ehEmAtendimento = statusEmAtendimentoConfig != null ? statusNum === statusEmAtendimentoConfig : false;
            const ehEmDesenvolvimento = statusEmDesenvolvimentoConfig != null ? statusNum === statusEmDesenvolvimentoConfig : nome.includes('desenvolv');
            const ehEmAnalise = statusEmAnaliseConfig != null ? statusNum === statusEmAnaliseConfig : false;
            const ehEmTestes = statusEmTesteConfig != null ? statusNum === statusEmTesteConfig : false;

            if (nome.includes('bloque')) {

            } else if (ehPausado) {
                pausados += 1;
            } else if (ehEmAtendimento) {
                emAtendimento += 1;
            } else if (ehEmDesenvolvimento) {
                emDesenvolvimento += 1;
            } else if (ehEmAnalise) {
                emAnalise += 1;
            } else if (ehEmTestes) {
                emTestes += 1;
            } else {
                abertos += 1;
            }
        });

        return {
            abertos,
            emAtendimento,
            emDesenvolvimento,
            emAnalise,
            emTestes,
            pausados,
            concluidosHoje: concluidosHojeApi
        };
    }, [data, dataComoColaborador, getNomeStatus, statusEmAtendimentoConfig, statusEmDesenvolvimentoConfig, statusEmAnaliseConfig, statusEmTesteConfig, statusPausadoConfig, concluidosHojeApi]);

    return (
        <div className="container-fluid tacoma-page home-lista-page">
            <ModalConfirm
                open={confirmPegar.show}
                onClose={() => setConfirmPegar({ show: false, sati: null })}
                title="Pegar chamado"
                message={`Você realmente deseja pegar o chamado ${confirmPegar.sati ? (confirmPegar.sati?.id ?? confirmPegar.sati) : ''}?`}
                confirmLabel="Sim, pegar"
                cancelLabel="Cancelar"
                onConfirm={executarPegarChamado}
                variant="primary"
            />
            <ModalConfirm
                open={confirmPausar.show}
                onClose={() => setConfirmPausar({ show: false, sati: null })}
                title="Pausar chamado"
                message={`Deseja pausar o chamado ${confirmPausar.sati ? (confirmPausar.sati?.id ?? confirmPausar.sati) : ''}?`}
                confirmLabel="Sim, pausar"
                cancelLabel="Cancelar"
                onConfirm={executarPausarChamado}
                variant="primary"
            />
            <ModalAlert
                open={alertMsg.show}
                onClose={() => setAlertMsg({ show: false, message: '' })}
                title="Aviso"
                message={alertMsg.message}
                labelOk="OK"
            />
            <ChamadosHeader
                search={search}
                onSearchChange={setSearch}
                loading={loading}
                onAtualizar={fetchData}
                onAutomatico={openAutomatico}
                filtrosAbertos={filtrosAbertos}
                onToggleFiltros={() => setFiltrosAbertos((v) => !v)}
                filtrosAtivos={filtrosAtivos}
                totalVisivel={totalVisivel}
                vista={vista}
                onVistaChange={setVista}
            />

            {filtrosAbertos && (
            <div className="chamados-filtros-card">
                <div className="chamados-filtros-header">
                    <span className="chamados-filtros-title">Filtros avançados</span>
                </div>
                <div className="chamados-filtros-grid">
                    <div className="chamados-filtro-field">
                        <label className="chamados-filtro-label" htmlFor="filtroDescricao">Descrição</label>
                        <input
                            id="filtroDescricao"
                            value={texto}
                            onChange={(e) => setTexto(e.target.value.toUpperCase())}
                            type="text"
                            className="form-control chamados-filtro-input"
                            placeholder="Buscar por descrição"
                            aria-label="Filtrar por descrição"
                        />
                    </div>
                    <div className="chamados-filtro-field">
                        <label className="chamados-filtro-label" htmlFor="filtroAtendente">Atendente</label>
                        <input
                            id="filtroAtendente"
                            value={colabo}
                            onChange={(e) => setColabo(e.target.value.toUpperCase())}
                            type="text"
                            className="form-control chamados-filtro-input"
                            placeholder="Nome ou código"
                            aria-label="Filtrar por atendente"
                        />
                    </div>
                    <div className="chamados-filtro-field">
                        <label className="chamados-filtro-label" htmlFor="filtroSolicitante">Solicitante</label>
                        <input
                            id="filtroSolicitante"
                            value={solici}
                            onChange={(e) => setSolici(e.target.value)}
                            type="text"
                            className="form-control chamados-filtro-input"
                            placeholder="Nome ou código"
                            aria-label="Filtrar por solicitante"
                        />
                    </div>
                    <div className="chamados-filtro-field chamados-filtro-equipe">
                        <label className="chamados-filtro-label">Escopo</label>
                        <div className="form-check form-check-inline tacoma-check-equipe">
                            <input
                                type="checkbox"
                                className="form-check-input"
                                id="checkEquipe"
                                checked={marcado}
                                onChange={handleCheckboxChange}
                            />
                            <label className="form-check-label" htmlFor="checkEquipe">Toda equipe</label>
                        </div>
                    </div>
                    <div className="chamados-filtro-actions">
                        <button
                            onClick={fetchData}
                            className="ax-btn ax-btn--primary"
                            type="button"
                            disabled={loading}
                            title="Aplicar filtros"
                        >
                            <i className={`fas fa-search ${loading ? 'fa-spin' : ''}`} />
                            {loading ? 'Filtrando...' : 'Aplicar filtros'}
                        </button>
                    </div>
                </div>
            </div>
            )}

            {erro && <div className="alert alert-danger tacoma-alert" role="alert">{erro}</div>}
            {isAutomatico && (
                <DrawerChamadoAutomatico
                    open={isAutomatico}
                    onClose={fecharAutomatico}
                    codAte={user}
                    onSuccess={fetchData}
                />
            )}

            <ChamadosMetrics metrics={metrics} />

            {vista === 'kanban' ? (
                <div className="lista-wrapper tacoma-card mt-3 chamados-kanban-wrap">
                    <ChamadosKanban
                        colunas={colunasKanban}
                        itensPorColuna={quadroKanban.mapa}
                        outros={quadroKanban.outros}
                        loading={loading || loadingStatus}
                        onMoverStatus={moverStatusKanban}
                    />
                </div>
            ) : listaResponsavelAberta ? (
                <div className="lista-wrapper tacoma-card mt-3">
                    <div className="d-flex justify-content-between align-items-center mb-2">
                        <h2 className="h6 mb-0">Meus chamados (como responsável)</h2>
                        <button
                            type="button"
                            className="btn btn-sm btn-outline-secondary"
                            onClick={() => setListaResponsavelAberta(false)}
                        >
                            <i className="fas fa-chevron-up" />
                        </button>
                    </div>
                    <ChamadosList
                        title="Meus chamados (como responsável)"
                        items={meusFiltrados}
                        loading={loading}
                        emptyMessage="Nenhum chamado encontrado para os filtros informados."
                        getNomeTipo={getNomeTipo}
                        getNomeStatus={getNomeStatus}
                        onIniciar={handleIniciarChamado}
                        onPausar={handlePausarChamado}
                        onEditar={handleEditarChamado}
                        onMoreActions={handleMoreActions}
                        onTrocarPrioridade={handleTrocarPrioridade}
                        statusCodigosEmContagem={statusCodigosEmContagem}
                    />
                </div>
            ) : (
                <div className="lista-wrapper tacoma-card mt-3" style={{ padding: '0.5rem 0.75rem' }}>
                    <div className="d-flex justify-content-between align-items-center">
                        <h2 className="h6 mb-0">Meus chamados (como responsável)</h2>
                        <button
                            type="button"
                            className="btn btn-sm btn-outline-secondary"
                            onClick={() => setListaResponsavelAberta(true)}
                        >
                            <i className="fas fa-chevron-down" />
                        </button>
                    </div>
                </div>
            )}

            {vista === 'lista' && listaColaboradorAberta ? (
                <div className="lista-wrapper tacoma-card mt-3">
                    <div className="d-flex justify-content-between align-items-center mb-2">
                        <h2 className="h6 mb-0">Chamados em que sou colaborador</h2>
                        <button
                            type="button"
                            className="btn btn-sm btn-outline-secondary"
                            onClick={() => setListaColaboradorAberta(false)}
                        >
                            <i className="fas fa-chevron-up" />
                        </button>
                    </div>
                    <ChamadosList
                        title="Chamados em que sou colaborador"
                        items={colaboradorFiltrados}
                        loading={loading}
                        emptyMessage="Nenhum chamado como colaborador para os filtros informados."
                        getNomeTipo={getNomeTipo}
                        getNomeStatus={getNomeStatus}
                        onIniciar={handleIniciarChamado}
                        onPausar={handlePausarChamado}
                        onEditar={handleEditarChamado}
                        onMoreActions={handleMoreActions}
                        onTrocarPrioridade={handleTrocarPrioridade}
                        statusCodigosEmContagem={statusCodigosEmContagem}
                    />
                </div>
            ) : vista === 'lista' ? (
                <div className="lista-wrapper tacoma-card mt-3" style={{ padding: '0.5rem 0.75rem' }}>
                    <div className="d-flex justify-content-between align-items-center">
                        <h2 className="h6 mb-0">Chamados em que sou colaborador</h2>
                        <button
                            type="button"
                            className="btn btn-sm btn-outline-secondary"
                            onClick={() => setListaColaboradorAberta(true)}
                        >
                            <i className="fas fa-chevron-down" />
                        </button>
                    </div>
                </div>
            ) : null}
        </div>
    );
}

function Home() {
    return (
        <div className="app-layout">
            <Sidebar />
            <main className="main-content home-main">
                <ChamadosPage />
            </main>
        </div>
    );
}

export default Home;
